import React, { useCallback, useEffect, useRef, useState } from 'react';
import Tab from '@mui/material/Tab';
import TabsBase from '@mui/material/Tabs';
import withCx, { CxProps } from 'fe-core/util/withCx';
import Box from 'fe-design-base/atoms/Box';
import Tooltip from 'fe-design-base/molecules/Tooltip';
import { detectTruncatedText } from 'fe-design-base/utils/detectTruncatedText';
import { partial } from 'lodash';

import { EVENT_ACTIONS, TRACK_ACTION_TYPES } from 'util/tracking_constants';
import { useTrackUx } from 'util/uxEvents';

import TabItem, { TabItemProps as PublicTabItemProps } from './TabItem';

export type OnChangeType = (
  event: React.SyntheticEvent,
  newValue: number | string
) => void;

export type TabOptionProps = PublicTabItemProps;

export interface TabsProps {
  options: TabOptionProps[];
  disabled?: boolean;
  onChange?: OnChangeType;
  tabValue?: number | string;
  scrollable?: boolean;
  ariaName?: string;
  uxElement?: string;
  dataTestId?: string;
  noPadding?: boolean;
}

const Tabs = ({
  ariaName,
  cx,
  cxEl,
  dataTestId = 'FDBTab',
  disabled,
  onChange,
  options,
  scrollable,
  tabValue,
  uxElement,
  noPadding,
}: TabsProps & CxProps) => {
  const tabRefs = useRef<HTMLButtonElement[]>([]);
  const [activeTab, setActiveTab] = React.useState<number | string>(
    tabValue || options[0].value
  );

  const [aTabHasFocus, setATabHasFocus] = useState(false);
  const [focusedTab, setFocusedTab] = useState<number | string>();
  const [overflowingTabs, setOverflowingTabs] = useState<Set<string | number>>(
    new Set()
  );

  const trackUx = useTrackUx();

  // Function to check overflow for all tabs
  const checkTabOverflow = useCallback(() => {
    const newOverflowingTabs = new Set<string | number>();

    tabRefs.current.forEach((tabRef, index) => {
      if (tabRef && options[index]) {
        // Find the text element within the tab that contains the label
        // Look for the Typography component (Material-UI) that contains the actual text
        const textElement = tabRef.querySelector(
          '.MuiTypography-root'
        ) as HTMLElement;
        const elementToCheck = textElement || tabRef;

        const isOverflowing = detectTruncatedText(elementToCheck);

        if (isOverflowing) {
          newOverflowingTabs.add(options[index].value);
        }
      }
    });

    setOverflowingTabs(newOverflowingTabs);
  }, [options]);

  useEffect(() => {
    if (tabValue !== undefined) setActiveTab(tabValue);
    if (!aTabHasFocus) setFocusedTab(undefined);
  }, [tabValue, aTabHasFocus]);

  // Check overflow on mount and when options change
  useEffect(() => {
    // Use a small delay to ensure DOM is fully rendered
    const timeoutId = setTimeout(checkTabOverflow, 0);
    return () => clearTimeout(timeoutId);
  }, [checkTabOverflow, options]);

  // Check overflow on window resize
  useEffect(() => {
    const handleResize = () => {
      checkTabOverflow();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [checkTabOverflow]);

  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: number | string) => {
      const selectedTab = options.find(option => option.value === newValue);

      if (disabled || selectedTab?.disabled) {
        event.preventDefault();
        return;
      }

      if (uxElement) {
        trackUx(EVENT_ACTIONS.TAB_CLICKED, TRACK_ACTION_TYPES.CLICK, {
          value: newValue,
          tabText: selectedTab?.label,
          icon: selectedTab?.iconName,
        });
      }

      setActiveTab(newValue);
      onChange?.(event, newValue);
    },
    [disabled, onChange, options, trackUx, uxElement]
  );

  const onSetTabRef = (element: HTMLElement | null) => {
    if (element) {
      tabRefs.current.push(element as HTMLButtonElement);
    }
  };

  const onFocusVisible = (value: number | string) => {
    setFocusedTab(value);
    setATabHasFocus(true);
  };

  const onBlurCapture = () => {
    /** Slowing this down to prevent race condition with onFocusVisible */
    setTimeout(() => {
      setATabHasFocus(
        tabRefs.current.some(tabRef => tabRef === document.activeElement)
      );
    }, 100);
  };

  return (
    <Box pv={noPadding ? 0 : 20}>
      <TabsBase
        className={cx({ hideActiveLine: activeTab === focusedTab })}
        value={activeTab}
        onChange={handleChange}
        aria-label={`${ariaName}-tabs`}
        data-testid={dataTestId}
        variant={scrollable ? 'scrollable' : 'fullWidth'}
        scrollButtons={false}
      >
        {options.map(tabDetails => {
          const { label, value, iconName } = tabDetails;
          const isOverflowing = overflowingTabs.has(value);

          const tabLabelContent = <TabItem {...tabDetails} />;

          return (
            <Tab
              key={`${label}-${value}`}
              value={value}
              ref={onSetTabRef}
              tabIndex={0}
              data-testid={`${dataTestId}-${value}`}
              aria-disabled={disabled || tabDetails.disabled}
              className={cxEl('tab', { iconName, scrollable })}
              onFocusVisible={partial(onFocusVisible, value)}
              onBlurCapture={onBlurCapture}
              label={
                isOverflowing ? (
                  <Tooltip content={label} variant="mono" placement="top">
                    {tabLabelContent}
                  </Tooltip>
                ) : (
                  tabLabelContent
                )
              }
            />
          );
        })}
      </TabsBase>
    </Box>
  );
};

export default withCx<TabsProps>('FDBTabs')(Tabs);
